import { get, patch, post, put, type RequestOptions } from './client'
import type {
  FundingIntentDto,
  PaymentsActivityDto,
  PaymentsSummaryDto,
  RedemptionDto,
} from './payments'
import type {
  AccountDto,
  CapacityEnvelopeDto,
  CatalogDto,
  CertificateListDto,
  ChainDto,
  DeploymentListDto,
  DeploymentDetailDto,
  EscrowDto,
  BidsDto,
  LeaseListDto,
  ProviderListDto,
  RentalCapabilityDto,
  RentalOffersDto,
  RentalQuoteDto,
  AdminUsersDto,
  MeDto,
  MyIpDto,
  LoginDto,
  OperatorContractDto,
  OperatorDeploymentsDto,
  OperatorFleetDto,
  OperatorLogDto,
  OperatorNodeDto,
  OperatorProviderDto,
  OrdersDto,
  ProviderDto,
  TxSimulationDto,
  IdentityListDto,
  WalletChallengeDto,
} from './types'
import type {
  VersionScanDetail,
  VersionScanHistory,
  VersionsResponse,
  StartVersionScanResponse,
} from './versions'

export const endpoints = {
  chain: (options?: { signal?: AbortSignal }) => get<ChainDto>('/api/tenant/chain', options),
  myIp: (options?: { signal?: AbortSignal }) => get<MyIpDto>('/api/tenant/network/my-ip', options),
  account: (address: string, options?: { signal?: AbortSignal }) =>
    get<AccountDto>(`/api/tenant/account/${encodeURIComponent(address)}`, options),
  providers: (options?: { signal?: AbortSignal }) => get<ProviderListDto>('/api/tenant/providers', options),
  provider: (address: string, options?: { signal?: AbortSignal }) =>
    get<ProviderDto>(`/api/tenant/providers/${encodeURIComponent(address)}`, options),
  catalog: (params: URLSearchParams = new URLSearchParams(), options?: { signal?: AbortSignal }) =>
    get<CatalogDto>(`/api/tenant/catalog${params.toString() ? `?${params}` : ''}`, options),
  capacityEnvelope: (options?: { signal?: AbortSignal }) =>
    get<CapacityEnvelopeDto>('/api/tenant/capacity/envelope', options),
  deployments: (address: string, options?: { signal?: AbortSignal }) =>
    get<DeploymentListDto>(`/api/tenant/deployments/${encodeURIComponent(address)}`, options),
  deployment: (address: string, dseq: string, options?: { signal?: AbortSignal }) =>
    get<DeploymentDetailDto>(`/api/tenant/deployments/${encodeURIComponent(address)}/${encodeURIComponent(dseq)}`, options),
  orders: (address: string, options?: { signal?: AbortSignal }) =>
    get<OrdersDto>(`/api/tenant/orders/${encodeURIComponent(address)}`, options),
  leases: (address: string, options?: { signal?: AbortSignal }) =>
    get<LeaseListDto>(`/api/tenant/leases/${encodeURIComponent(address)}`, options),
  certificates: (address: string, options?: { signal?: AbortSignal }) =>
    get<CertificateListDto>(`/api/tenant/certificates/${encodeURIComponent(address)}`, options),
  escrow: (address: string, options?: { signal?: AbortSignal }) =>
    get<EscrowDto>(`/api/tenant/escrow/${encodeURIComponent(address)}`, options),
  bids: (address: string, dseq: string, options?: { signal?: AbortSignal }) =>
    get<BidsDto>(`/api/tenant/bids/${encodeURIComponent(address)}?dseq=${encodeURIComponent(dseq)}`, options),
  broadcast: (body: unknown, options?: { signal?: AbortSignal }) =>
    post<{ accepted: boolean; code: number; txhash: string; raw_log: string }>(
      '/api/tenant/tx/broadcast',
      body,
      options,
    ),
  simulate: (body: unknown, options?: RequestOptions) =>
    post<TxSimulationDto>('/api/tenant/tx/simulate', body, options),
  txStatus: (hash: string, options?: { signal?: AbortSignal }) =>
    get<{ found: boolean; succeeded?: boolean; code?: number; raw_log?: string }>(
      `/api/tenant/tx/${encodeURIComponent(hash)}`,
      options,
    ),
  providerRelay: (provider: string, path: string, body: unknown, token: string, options?: RequestOptions) =>
    post<unknown>(`/api/tenant/provider/${encodeURIComponent(provider)}/${path.replace(/^\/+/, '')}`, body, { ...options, headers: { ...(options?.headers ?? {}), Authorization: `Bearer ${token}` } }),
  // The provider's route is `PUT /deployment/{dseq}/manifest` — the owner comes
  // from the signed token, not the path — and its body is manifest JSON
  // (`workloadManifest`), not SDL.
  providerManifest: (provider: string, dseq: string, manifestJson: string, token: string, options?: RequestOptions) =>
    put<unknown>(`/api/tenant/provider/${encodeURIComponent(provider)}/deployment/${encodeURIComponent(dseq)}/manifest`, manifestJson, { ...options, headers: { ...(options?.headers ?? {}), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }),
  // The provider's own status: its cluster's public address among the rest.
  // Unauthenticated at the provider; the relay still wants the tenant's bearer.
  providerStatus: (provider: string, token: string, options?: RequestOptions) =>
    get<unknown>(`/api/tenant/provider/${encodeURIComponent(provider)}/status`, { ...options, headers: { ...(options?.headers ?? {}), Authorization: `Bearer ${token}` } }),
  providerLeaseStatus: (provider: string, dseq: string, gseq: number, oseq: number, token: string, options?: RequestOptions) =>
    get<unknown>(`/api/tenant/provider/${encodeURIComponent(provider)}/lease/${encodeURIComponent(dseq)}/${gseq}/${oseq}/status`, { ...options, headers: { ...(options?.headers ?? {}), Authorization: `Bearer ${token}` } }),
  // Direct rental. These go through the console's own server-side proxy, not
  // the wallet-signed provider relay: the provider's rental endpoints are
  // unauthenticated, and a tenant should not have to connect a wallet and
  // sign before they can see what is for rent.
  rentalCapability: (provider: string, options?: { signal?: AbortSignal }) =>
    get<RentalCapabilityDto>(`/api/tenant/rental/${encodeURIComponent(provider)}/capability`, options),
  rentalOffers: (provider: string, options?: { signal?: AbortSignal }) =>
    get<RentalOffersDto>(`/api/tenant/rental/${encodeURIComponent(provider)}/offers`, options),
  rentalQuote: (provider: string, body: unknown, options?: RequestOptions) =>
    post<RentalQuoteDto>(`/api/tenant/rental/${encodeURIComponent(provider)}/quote`, body, options),
  me: (options?: { signal?: AbortSignal }) => get<MeDto>('/api/me', options),
  login: (body: { username?: string; email?: string; password: string }, options?: RequestOptions) => post<LoginDto>('/login', body, options),
  signup: (body: { email: string; password: string }, options?: RequestOptions) => post<LoginDto>('/api/auth/signup', body, options),
  cosmosChallenge: (body: { address: string; chain_id: string }, options?: RequestOptions) => post<WalletChallengeDto>('/api/auth/cosmos/challenge', body, options),
  cosmosVerify: (body: { address: string; chain_id: string; message: string; signature: string; pub_key: string; pub_key_type?: string }, options?: RequestOptions) => post<LoginDto>('/api/auth/cosmos/verify', body, options),
  identities: (options?: { signal?: AbortSignal }) => get<IdentityListDto>('/api/auth/identities', options),
  // The payments surface. Every one of these is proxied by the console to the
  // billing service on loopback; none of them talks to the chain, and none
  // carries a wallet address — the server derives it from the linked identity.
  payments: {
    summary: (options?: { signal?: AbortSignal }) =>
      get<PaymentsSummaryDto>('/api/tenant/payments/summary', options),
    complianceSession: (options?: RequestOptions) =>
      post<{ reference: string; url: string; status: string }>('/api/tenant/payments/compliance/session', {}, options),
    createFundingIntent: (body: { amount_cents: number; idempotency_key: string; return_path?: string }, options?: RequestOptions) =>
      post<FundingIntentDto>('/api/tenant/payments/funding-intents', body, options),
    fundingIntent: (id: string, options?: { signal?: AbortSignal }) =>
      get<FundingIntentDto>(`/api/tenant/payments/funding-intents/${encodeURIComponent(id)}`, options),
    cancelFundingIntent: (id: string, options?: RequestOptions) =>
      post<FundingIntentDto>(`/api/tenant/payments/funding-intents/${encodeURIComponent(id)}/cancel`, {}, options),
    activity: (options?: { signal?: AbortSignal }) =>
      get<PaymentsActivityDto>('/api/tenant/payments/activity', options),
    createRedemption: (body: { amount_cents: number }, options?: RequestOptions) =>
      post<RedemptionDto>('/api/tenant/payments/redemptions', body, options),
    redemption: (id: string, options?: { signal?: AbortSignal }) =>
      get<RedemptionDto>(`/api/tenant/payments/redemptions/${encodeURIComponent(id)}`, options),
    submitBurnProof: (id: string, body: { tx_hash: string }, options?: RequestOptions) =>
      post<RedemptionDto>(`/api/tenant/payments/redemptions/${encodeURIComponent(id)}/burn-proof`, body, options),
  },
  operator: {
    nodes: (options?: { signal?: AbortSignal }) => get<OperatorNodeDto[]>('/api/nodes', options),
    providers: (options?: { signal?: AbortSignal }) => get<OperatorProviderDto[]>('/api/providers', options),
    fleet: (options?: { signal?: AbortSignal }) => get<OperatorFleetDto>('/api/provider-fleet', options),
    contract: (options?: { signal?: AbortSignal }) => get<OperatorContractDto>('/api/contract', options),
    logs: (options?: { signal?: AbortSignal }) => get<OperatorLogDto[]>('/api/logs', options),
    deployments: (options?: { signal?: AbortSignal }) => get<OperatorDeploymentsDto>('/api/custom-deployments', options),
    pods: (options?: { signal?: AbortSignal }) => get<unknown[]>('/api/k8s/pods', options),
    services: (options?: { signal?: AbortSignal }) => get<unknown[]>('/api/k8s/services', options),
    nodeports: (options?: { signal?: AbortSignal }) => get<unknown[]>('/api/k8s/nodeports', options),
    users: (options?: { signal?: AbortSignal }) => get<AdminUsersDto>('/api/admin/users', options),
    updateUserRole: (userId: number, role: string, options?: RequestOptions) => patch<{ status: string; user_id: number; role: string }>(`/api/admin/users/${userId}`, { role }, options),
    providerPower: (providerId: number, action: 'stop' | 'start' | 'restart' | 'reboot', options?: RequestOptions) =>
      post<{ status?: string; provider_id?: number; host?: string; action?: string; error?: string }>(`/api/provider/${providerId}/power`, { action }, options),
    action: (name: string, body: unknown = {}, options?: RequestOptions) => post<{ status?: string; error?: string }>(`/api/action/${encodeURIComponent(name)}`, body, options),
  },
  versions: {
    latest: (options?: { signal?: AbortSignal }) => get<VersionsResponse>('/api/ops/versions', options),
    scan: (scanId: string, options?: { signal?: AbortSignal }) =>
      get<VersionScanDetail>(`/api/ops/version-scans/${encodeURIComponent(scanId)}`, options),
    history: (params: { limit?: number; cursor?: string } = {}, options?: { signal?: AbortSignal }) => {
      const query = new URLSearchParams()
      if (params.limit !== undefined) query.set('limit', String(params.limit))
      if (params.cursor !== undefined) query.set('cursor', params.cursor)
      return get<VersionScanHistory>(`/api/ops/version-scans${query.toString() ? `?${query}` : ''}`, options)
    },
    startScan: (options?: RequestOptions) => post<StartVersionScanResponse>('/api/ops/version-scans', {}, options),
  },
}
